import React, { useState, useEffect } from 'react';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

const COUNTRY_DATA = {
  sa: { code: '+966', flag: '🇸🇦', name: 'السعودية', maxLength: 9 },
  ae: { code: '+971', flag: '🇦🇪', name: 'الإمارات', maxLength: 9 },
  kw: { code: '+965', flag: '🇰🇼', name: 'الكويت', maxLength: 8 },
  bh: { code: '+973', flag: '🇧🇭', name: 'البحرين', maxLength: 8 },
  om: { code: '+968', flag: '🇴🇲', name: 'عمان', maxLength: 8 },
  qa: { code: '+974', flag: '🇶🇦', name: 'قطر', maxLength: 8 }
};

export default function PhoneInput({ value, onChange, error }: PhoneInputProps) {
  const [selectedCountry, setSelectedCountry] = useState('sa');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const fullNumber = COUNTRY_DATA[selectedCountry as keyof typeof COUNTRY_DATA].code + phoneNumber;
    onChange(fullNumber);
  }, [selectedCountry, phoneNumber, onChange]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountry(e.target.value);
    setPhoneNumber('');
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\D/g, '');
    const maxLength = COUNTRY_DATA[selectedCountry as keyof typeof COUNTRY_DATA].maxLength;
    setPhoneNumber(input.slice(0, maxLength));
  };

  return (
    <div className="phone-input-container" dir="ltr">
      <div 
        className={`flex items-center border rounded-lg overflow-hidden bg-white ${
          error ? 'border-red-500' : 
          isFocused ? 'ring-2 ring-green-500 border-transparent' : 'border-gray-300'
        }`}
      >
        <div className="flex items-center px-3 border-l border-gray-200">
          <span className="text-xl ml-1" aria-hidden="true">
            {COUNTRY_DATA[selectedCountry as keyof typeof COUNTRY_DATA].flag}
          </span>
          <select
            value={selectedCountry}
            onChange={handleCountryChange}
            className="appearance-none bg-transparent border-none focus:ring-0 py-2 pl-1 pr-6 text-gray-700"
            aria-label="اختر الدولة"
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          >
            {Object.entries(COUNTRY_DATA).map(([code, data]) => (
              <option key={code} value={code}>
                {data.code}
              </option>
            ))}
          </select>
        </div>
        <input
          type="tel"
          inputMode="numeric"
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder="رقم الجوال"
          className="flex-1 px-3 py-2 border-none focus:ring-0 text-base"
          aria-label="رقم الجوال"
          maxLength={COUNTRY_DATA[selectedCountry as keyof typeof COUNTRY_DATA].maxLength}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          pattern="[0-9]*"
        />
      </div>
      <div className="text-sm mt-1 text-right" dir="rtl">
        <span className={error ? 'text-red-500' : 'text-gray-500'}>
          {`أدخل ${COUNTRY_DATA[selectedCountry as keyof typeof COUNTRY_DATA].maxLength} أرقام`}
        </span>
      </div>
    </div>
  );
}